import axios from 'axios';
import { stringify } from 'javascript-stringify';

interface PostData {
  width: number;
  height: number;
  chart: string;
  format?: string;
  backgroundColor?: string;
  devicePixelRatio?: number;
  version?: string;
}

class ChartJsImage {
  private host: string;
  private protocol: string;
  private baseUrl: string;
  private chart?: string;
  private width: number;
  private height: number;
  private devicePixelRatio: number;
  private backgroundColor: string;
  private format: string;
  private version: string;

  constructor() {
    this.host = 'quickchart.io';
    this.protocol = 'https';
    this.baseUrl = `${this.protocol}://${this.host}`;

    this.chart = undefined;
    this.width = 500;
    this.height = 300;
    this.devicePixelRatio = 2.0;
    this.backgroundColor = '#ffffff';
    this.format = 'png';
    this.version = '4.4.2';
  }

  setConfig(chartConfig: unknown): this {
    this.chart = stringify(chartConfig);
    return this;
  }

  setWidth(width: number | string): this {
    this.width = typeof width === 'string' ? parseInt(width, 10) : width;
    return this;
  }

  setHeight(height: number | string): this {
    this.height = typeof height === 'string' ? parseInt(height, 10) : height;
    return this;
  }

  private isValid(): boolean {
    return Boolean(this.chart);
  }

  private getPostData(): PostData {
    const {
      width,
      height,
      chart,
      format,
      backgroundColor,
      devicePixelRatio,
      version,
    } = this;

    const postData: PostData = {
      width,
      height,
      chart: chart || '',
    };

    if (format) postData.format = format;
    if (backgroundColor) postData.backgroundColor = backgroundColor;
    if (devicePixelRatio) postData.devicePixelRatio = devicePixelRatio;
    if (version) postData.version = version;

    return postData;
  }

  async toBinary(): Promise<Buffer> {
    if (!this.isValid()) {
      throw new Error('You must call setConfig before calling toBinary');
    }

    const response = await axios.post(
      'https://quickchart.io/chart',
      this.getPostData(),
      {
        responseType: 'arraybuffer',
      }
    );

    if (response.status !== 200) {
      throw new Error(
        `Bad response code ${response.status} from chart endpoint`
      );
    }

    return Buffer.from(response.data, 'binary');
  }

  async toDataUrl(): Promise<string> {
    const buffer = await this.toBinary();
    const base64 = buffer.toString('base64');
    return `data:image/png;base64,${base64}`;
  }
}

export default ChartJsImage;
